import { useCallback, useEffect, useState } from "react";
import { get } from "../lib/api";

export const useData = (path) => {
  // State is keyed by path so a route change never renders the previous
  // resource (e.g. going from a real article to /aktuelt/finnes-ikke).
  const [state, setState] = useState({ path: null, data: null, status: "idle" });
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    if (!path) return;
    let alive = true;
    setState((s) => (s.path === path ? { ...s, status: s.data ? s.status : "loading" } : { path, data: null, status: "loading" }));
    get(path)
      .then((d) => { if (alive) setState({ path, data: d, status: "ok" }); })
      .catch((e) => { if (alive) setState({ path, data: null, status: e?.response?.status === 404 ? "notfound" : "error" }); });
    return () => { alive = false; };
  }, [path, nonce]);
  const reload = useCallback(() => setNonce((n) => n + 1), []);
  if (!path) return { data: null, status: "idle", reload };
  const fresh = state.path === path;
  return { data: fresh ? state.data : null, status: fresh ? state.status : "loading", reload };
};

export const usePageMeta = (title, description) => {
  useEffect(() => {
    if (title) document.title = title.includes("EQVA") ? title : `${title} – EQVA ASA`;
    if (description) {
      let m = document.querySelector('meta[name="description"]');
      if (!m) { m = document.createElement("meta"); m.setAttribute("name", "description"); document.head.appendChild(m); }
      m.setAttribute("content", description);
    }
  }, [title, description]);
};

/** Scroll progress 0..1 of an element (for reading progress). */
export const useReadProgress = (ref) => {
  const [p, setP] = useState(0);
  useEffect(() => {
    let raf = 0;
    const on = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const el = ref.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const total = r.height - window.innerHeight * 0.6;
        setP(Math.max(0, Math.min(1, (-r.top + window.innerHeight * 0.2) / (total || 1))));
      });
    };
    on();
    window.addEventListener("scroll", on, { passive: true });
    window.addEventListener("resize", on);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("scroll", on); window.removeEventListener("resize", on); };
  }, [ref]);
  return p;
};
